import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { channelPartnerAuthAPI } from '../../services/api';
import { Card, Button, Spinner, Badge, EmptyState, PageHeader, Modal } from '../../components/UI';
import { HiOutlineTicket, HiOutlinePlus, HiOutlinePaperAirplane, HiOutlinePaperClip, HiOutlineDownload } from 'react-icons/hi';

const formatDateTime = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};
const formatDate = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const PRIORITY_VARIANT = { low: 'default', medium: 'info', high: 'warning', urgent: 'danger' };

const inputCls = 'w-full rounded-xl border-2 border-secondary-200 px-3.5 py-2.5 text-sm text-secondary-900 focus:border-primary-500 focus:outline-none';

const emptyCreateForm = { subject: '', description: '', category: '', priority: 'medium' };

const PartnerSupport = () => {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ ...emptyCreateForm });
  const [createFiles, setCreateFiles] = useState([]);
  const [activeTicketId, setActiveTicketId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyFiles, setReplyFiles] = useState([]);

  const { data, isLoading } = useQuery({
    queryKey: ['partner-support-tickets'],
    queryFn: () => channelPartnerAuthAPI.getSupportTickets(),
  });
  const tickets = data?.data?.data || [];

  const { data: categoriesData } = useQuery({
    queryKey: ['partner-support-categories'],
    queryFn: () => channelPartnerAuthAPI.getSupportCategories(),
    enabled: showCreate,
  });
  const categories = categoriesData?.data?.data || [];

  const { data: ticketData, isLoading: ticketLoading } = useQuery({
    queryKey: ['partner-support-ticket', activeTicketId],
    queryFn: () => channelPartnerAuthAPI.getSupportTicket(activeTicketId),
    enabled: !!activeTicketId,
  });
  const activeTicket = ticketData?.data?.data;

  const createMutation = useMutation({
    mutationFn: (payload) => {
      if (createFiles.length === 0) return channelPartnerAuthAPI.createSupportTicket(payload);
      const formData = new FormData();
      Object.entries(payload).forEach(([k, v]) => v !== undefined && v !== '' && formData.append(k, v));
      createFiles.forEach((f) => formData.append('files', f));
      return channelPartnerAuthAPI.createSupportTicketWithFiles(formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['partner-support-tickets']);
      toast.success('Ticket raised');
      setShowCreate(false);
      setCreateForm({ ...emptyCreateForm });
      setCreateFiles([]);
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to raise ticket'),
  });

  const replyMutation = useMutation({
    mutationFn: ({ id, message, files }) => {
      if (!files || files.length === 0) return channelPartnerAuthAPI.replySupportTicket(id, { message });
      const formData = new FormData();
      formData.append('message', message);
      files.forEach((f) => formData.append('files', f));
      return channelPartnerAuthAPI.replySupportTicketWithFiles(id, formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['partner-support-tickets']);
      queryClient.invalidateQueries(['partner-support-ticket', activeTicketId]);
      setReplyText('');
      setReplyFiles([]);
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to send reply'),
  });

  const handleCreate = (e) => {
    e.preventDefault();
    if (!createForm.subject.trim() || !createForm.description.trim()) return;
    createMutation.mutate(createForm);
  };

  const handleReply = (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    replyMutation.mutate({ id: activeTicketId, message: replyText.trim(), files: replyFiles });
  };

  return (
    <div>
      <PageHeader
        title="Support"
        description="Raise a ticket and our team will get back to you"
        action={
          <Button variant="primary" icon={<HiOutlinePlus className="w-4 h-4" />} onClick={() => setShowCreate(true)}>
            New Ticket
          </Button>
        }
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-12"><Spinner /></div>
      ) : tickets.length === 0 ? (
        <Card>
          <EmptyState
            icon={<HiOutlineTicket className="w-7 h-7 text-secondary-400" />}
            title="No tickets yet"
            description="Raise a ticket if you have a question or run into an issue."
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {tickets.map((t) => (
            <Card key={t._id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTicketId(t._id)}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs text-secondary-400 font-mono">{t.ticketNumber}</p>
                  <p className="font-semibold text-secondary-900 text-sm truncate">{t.subject}</p>
                  <p className="text-xs text-secondary-400 mt-1">{formatDate(t.createdAt)}</p>
                </div>
                <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                  {t.stage && (
                    <span
                      className="text-xs font-semibold px-2 py-0.5 rounded-lg"
                      style={{ backgroundColor: `${t.stage.color}1A`, color: t.stage.color }}
                    >
                      {t.stage.name}
                    </span>
                  )}
                  <Badge variant={PRIORITY_VARIANT[t.priority] || 'default'}>{t.priority}</Badge>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* New ticket modal */}
      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Raise a Support Ticket" size="md">
        <form onSubmit={handleCreate} className="p-5 space-y-4">
          <div className="space-y-1.5">
            <label className="block text-sm font-semibold text-secondary-700">Subject</label>
            <input
              type="text"
              value={createForm.subject}
              onChange={(e) => setCreateForm({ ...createForm, subject: e.target.value })}
              placeholder="e.g. Payout not received"
              className={inputCls}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-secondary-700">Category</label>
              <select
                value={createForm.category}
                onChange={(e) => setCreateForm({ ...createForm, category: e.target.value })}
                className={inputCls}
              >
                <option value="">Select...</option>
                {categories.map((c) => (
                  <option key={c._id} value={c._id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-secondary-700">Priority</label>
              <select
                value={createForm.priority}
                onChange={(e) => setCreateForm({ ...createForm, priority: e.target.value })}
                className={inputCls}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm font-semibold text-secondary-700">Description</label>
            <textarea
              rows={4}
              value={createForm.description}
              onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
              placeholder="Describe your issue or question..."
              className={inputCls}
              required
            />
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm font-semibold text-secondary-700">Attachments (optional)</label>
            <input
              type="file"
              multiple
              onChange={(e) => setCreateFiles(Array.from(e.target.files || []))}
              className="block w-full text-sm text-secondary-600"
            />
          </div>
          <Button type="submit" variant="primary" loading={createMutation.isPending} disabled={createMutation.isPending}>
            Submit Ticket
          </Button>
        </form>
      </Modal>

      {/* Ticket thread modal */}
      <Modal isOpen={!!activeTicketId} onClose={() => setActiveTicketId(null)} title={activeTicket?.subject || 'Ticket'} size="lg">
        {ticketLoading || !activeTicket ? (
          <div className="flex items-center justify-center py-12"><Spinner /></div>
        ) : (
          <div className="p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs text-secondary-400 font-mono">{activeTicket.ticketNumber}</p>
              <div className="flex items-center gap-2">
                {activeTicket.stage && (
                  <span
                    className="text-xs font-semibold px-2 py-0.5 rounded-lg"
                    style={{ backgroundColor: `${activeTicket.stage.color}1A`, color: activeTicket.stage.color }}
                  >
                    {activeTicket.stage.name}
                  </span>
                )}
                <Badge variant={PRIORITY_VARIANT[activeTicket.priority] || 'default'}>{activeTicket.priority}</Badge>
              </div>
            </div>

            <div className="space-y-3 max-h-80 overflow-y-auto mb-4">
              {activeTicket.activities.map((a, i) => {
                const fromSuperAdmin = a.meta === 'superadmin';
                return (
                <div key={a._id || i} className={`flex ${fromSuperAdmin ? 'justify-start' : 'justify-end'}`}>
                  <div
                    className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm ${
                      fromSuperAdmin ? 'bg-secondary-100 text-secondary-800' : 'bg-primary-500 text-white'
                    }`}
                  >
                    <p>{a.message}</p>
                    {a.attachments?.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {a.attachments.map((att) => (
                          <a
                            key={att._id}
                            href={att.url}
                            target="_blank"
                            rel="noreferrer"
                            className={`flex items-center gap-1.5 text-xs underline ${fromSuperAdmin ? 'text-primary-600' : 'text-primary-100'}`}
                          >
                            <HiOutlineDownload className="w-3.5 h-3.5" /> {att.originalName}
                          </a>
                        ))}
                      </div>
                    )}
                    <p className={`text-[11px] mt-1 ${fromSuperAdmin ? 'text-secondary-400' : 'text-primary-100'}`}>
                      {a.createdByName} · {formatDateTime(a.createdAt)}
                    </p>
                  </div>
                </div>
                );
              })}
            </div>

            <form onSubmit={handleReply} className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Type a reply..."
                  className={inputCls}
                />
                <label className="flex items-center justify-center w-11 rounded-xl border-2 border-secondary-200 cursor-pointer hover:border-primary-400 flex-shrink-0">
                  <HiOutlinePaperClip className="w-4 h-4 text-secondary-500" />
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => setReplyFiles(Array.from(e.target.files || []))}
                  />
                </label>
                <Button type="submit" variant="primary" icon={<HiOutlinePaperAirplane className="w-4 h-4" />} loading={replyMutation.isPending} disabled={replyMutation.isPending || !replyText.trim()}>
                  Send
                </Button>
              </div>
              {replyFiles.length > 0 && (
                <p className="text-xs text-secondary-400">{replyFiles.length} file(s) attached</p>
              )}
            </form>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PartnerSupport;
